import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calculator, ShieldWarning, ArrowsClockwise, Info, MagnifyingGlass, Warning } from '@phosphor-icons/react';
import { DepartmentNav } from '../components/DepartmentNav';
import { AmazonDisclosure } from '../components/AmazonDisclosure';
import { UpgradeGate } from '../components/UpgradeGate';
import { PageWhy } from '../components/PageWhy';
import { useZones } from '../hooks/useZones';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import axios from 'axios';
import { toast } from 'sonner';

import { API_URL } from '../config/api';

// FRAC Education content
const FracInfoButton = () => (
  <Popover>
    <PopoverTrigger asChild>
      <button
        data-testid="frac-info-btn"
        className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 hover:bg-purple-200 dark:hover:bg-purple-800/60 transition-colors ml-1 align-middle"
        aria-label="What is FRAC?"
      >
        <Info weight="bold" size={13} />
      </button>
    </PopoverTrigger>
    <PopoverContent className="w-80 text-sm" side="bottom" align="start">
      <div className="space-y-2">
        <h4 className="font-bold text-base">What is a FRAC Group?</h4>
        <p className="text-muted-foreground">
          <strong>FRAC</strong> stands for <strong>Fungicide Resistance Action Committee</strong>. It classifies fungicides by their <em>mode of action</em> — how they attack the disease.
        </p>
        <div className="border-t pt-2 space-y-1.5">
          <p className="font-semibold text-xs uppercase text-muted-foreground tracking-wide">Why Rotate FRAC Groups?</p>
          <p className="text-muted-foreground">
            Using the same FRAC group repeatedly lets the disease build resistance. By rotating to a different FRAC number each application, you keep all your fungicides effective long-term.
          </p>
        </div>
        <div className="border-t pt-2 space-y-1">
          <p className="font-semibold text-xs uppercase text-muted-foreground tracking-wide">Common Groups</p>
          <div className="grid grid-cols-2 gap-1 text-xs">
            <span><strong className="text-red-600">FRAC 1</strong> — MBCs (Thiophanate)</span>
            <span><strong className="text-blue-600">FRAC 2</strong> — Dicarboximides</span>
            <span><strong className="text-green-600">FRAC 3</strong> — DMIs (Propiconazole)</span>
            <span><strong className="text-purple-600">FRAC 7</strong> — SDHIs (Fluxapyroxad)</span>
            <span><strong className="text-orange-600">FRAC 11</strong> — Strobilurins (Azoxy)</span>
            <span><strong className="text-teal-600">FRAC 12</strong> — Phenylpyrroles</span>
          </div>
        </div>
        <p className="text-xs text-muted-foreground/70 italic pt-1">
          Tip: TurfPulse tracks your FRAC rotation automatically — just log your applications.
        </p>
      </div>
    </PopoverContent>
  </Popover>
);

// FRAC group color mapping for rotation planning
const FRAC_COLORS = {
  '1': { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-800 dark:text-red-300', label: 'FRAC 1 - MBCs' },
  '2': { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-800 dark:text-blue-300', label: 'FRAC 2 - Dicarboximides' },
  '3': { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-800 dark:text-green-300', label: 'FRAC 3 - DMIs' },
  '7': { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-800 dark:text-purple-300', label: 'FRAC 7 - SDHIs' },
  '11': { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-800 dark:text-orange-300', label: 'FRAC 11 - QoIs (Strobilurins)' },
  '12': { bg: 'bg-teal-100 dark:bg-teal-900/30', text: 'text-teal-800 dark:text-teal-300', label: 'FRAC 12 - Phenylpyrroles' },
  'M': { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-800 dark:text-gray-300', label: 'Multi-site (Low Resistance Risk)' },
};

export const DiseaseManagementPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(['products', 'common', 'dashboard']);
  const [products, setProducts] = useState([]);
  const [applications, setApplications] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showApplicationDialog, setShowApplicationDialog] = useState(false);
  const [productSearch, setProductSearch] = useState('');

  // Filter products based on search
  const filteredProducts = useMemo(() => {
    if (!productSearch.trim()) return products;
    const search = productSearch.toLowerCase();
    return products.filter(p => 
      p.name?.toLowerCase().includes(search) ||
      p.active_ingredient?.toLowerCase().includes(search) ||
      p.manufacturer?.toLowerCase().includes(search) ||
      p.target_diseases?.some(disease => disease.toLowerCase().includes(search)) ||
      p.frac_group?.toLowerCase().includes(search)
    );
  }, [products, productSearch]);

  // Calculator state
  const [area, setArea] = useState('');
  const [areaUnit, setAreaUnit] = useState('sqft');
  const [applicationRate, setApplicationRate] = useState('');
  const [calculatedAmount, setCalculatedAmount] = useState(null);
  const [calculatedCost, setCalculatedCost] = useState(null);

  // Application form
  const [appArea, setAppArea] = useState('');
  const [appRate, setAppRate] = useState('');
  const [appNotes, setAppNotes] = useState('');
  const [appZoneId, setAppZoneId] = useState('');
  const [appMethod, setAppMethod] = useState('spray');
  const [appSpreaderSetting, setAppSpreaderSetting] = useState('');
  const zones = useZones();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, applicationsRes] = await Promise.all([
        axios.get(`${API_URL}/api/disease/products`, { withCredentials: true }),
        axios.get(`${API_URL}/api/applications`, { withCredentials: true })
      ]);
      setProducts(productsRes.data);
      const appLogs = applicationsRes.data?.logs || applicationsRes.data || [];
      setApplications(appLogs.filter(app =>
        app.product_type === 'Fungicide' || app.product_type === 'Preventive Fungicide' || app.product_type === 'Curative Fungicide'
      ));
    } catch (error) {
      if (error.response?.status === 403) {
        // UpgradeGate handles the UI - just silently catch
      } else {
        console.error('Error fetching data:', error);
      }
    }
  };

  const calculateApplication = () => {
    if (!selectedProduct || !area || !applicationRate) {
      toast.error('Please fill in all fields');
      return;
    }
    const areaSqFt = areaUnit === 'acres' ? parseFloat(area) * 43560 : parseFloat(area);
    const rate = parseFloat(applicationRate);
    const amountNeeded = (areaSqFt / 1000) * rate;
    setCalculatedAmount(amountNeeded);
    if (selectedProduct.cost_per_unit) {
      setCalculatedCost(amountNeeded * selectedProduct.cost_per_unit);
    } else {
      setCalculatedCost(null);
    }
  };

  const handleLogApplication = async (e) => {
    e.preventDefault();
    if (!selectedProduct) {
      toast.error('Please select a product');
      return;
    }
    try {
      const rateUnit = appMethod === 'granular' ? (selectedProduct.rate_unit || 'lbs') : (selectedProduct.rate_unit || 'fl oz');
      await axios.post(
        `${API_URL}/api/applications`,
        {
          product_name: selectedProduct.name,
          product_type: selectedProduct.product_type,
          application_rate: `${appRate} ${rateUnit}/1000 sq ft on ${appArea} ${areaUnit}`,
          application_method: appMethod,
          spreader_setting: appMethod === 'granular' ? appSpreaderSetting || null : null,
          notes: `FRAC ${selectedProduct.frac_group || 'N/A'} | ${appNotes}`,
          zone_id: appZoneId || null,
          zone_name: zones.find(z => z.id === appZoneId)?.name || null
        },
        { withCredentials: true }
      );
      toast.success('Application logged successfully!');
      setShowApplicationDialog(false);
      setAppArea('');
      setAppRate('');
      setAppNotes('');
      setAppZoneId('');
      setAppMethod('spray');
      setAppSpreaderSetting('');
      fetchData();
    } catch (error) {
      toast.error('Failed to log application');
    }
  };

  const getFracColor = (frac) => {
    return FRAC_COLORS[frac] || { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-800 dark:text-gray-300', label: `FRAC ${frac}` };
  };

  const getTypeStyle = (type) => {
    switch (type) {
      case 'Preventive Fungicide': return 'bg-blue-100 text-blue-800';
      case 'Curative Fungicide': return 'bg-red-100 text-red-800';
      case 'Contact Fungicide': return 'bg-amber-100 text-amber-800';
      default: return 'bg-purple-100 text-purple-800';
    }
  };

  // Build FRAC rotation history from applications
  const recentFracGroups = applications
    .slice(0, 5)
    .map(app => {
      const fracMatch = app.notes?.match(/FRAC (\w+)/);
      return fracMatch ? fracMatch[1] : null;
    })
    .filter(Boolean);

  return (
    <div data-testid="disease-management-page" className="min-h-screen bg-background">
      <DepartmentNav />
      <UpgradeGate feature="disease_department" featureLabel="Disease Management Department">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Page Title */}
        <div className="flex items-center justify-between">
          <div>
            <h2 data-testid="disease-page-title" className="text-3xl font-bold flex items-center gap-2">
              <ShieldWarning weight="duotone" size={32} className="text-purple-600" />
              Disease Management Department
            </h2>
            <p className="text-muted-foreground">Fungicide rotation, FRAC group tracking, and disease pressure management <FracInfoButton /></p>
            <PageWhy testId="disease-page-why">Rotate FRAC groups automatically so your fungicides keep working — no resistance, no mystery brown patches that come back every year.</PageWhy>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Applications</CardTitle>
            </CardHeader>
            <CardContent>
              <div data-testid="disease-total-applications" className="text-3xl font-bold">{applications.length}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Products Available</CardTitle>
            </CardHeader>
            <CardContent>
              <div data-testid="disease-products-count" className="text-3xl font-bold">{products.length}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">FRAC Groups Used <FracInfoButton /></CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-purple-600">
                {new Set(recentFracGroups).size}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Unique Products</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">
                {new Set(products.map(p => p.frac_group)).size} FRAC Groups
              </div>
            </CardContent>
          </Card>
        </div>

        {/* FRAC Rotation Reminder */}
        {recentFracGroups.length > 0 && (
          <Card className="border-purple-200 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-900/20">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-3">
                <ArrowsClockwise weight="bold" size={18} className="text-purple-600" />
                <span className="font-medium text-purple-800 dark:text-purple-300">FRAC Rotation Tracker <FracInfoButton /></span>
              </div>
              <p className="text-sm text-purple-700 mb-3">Recent FRAC groups applied (newest first):</p>
              <div className="flex gap-2 flex-wrap">
                {recentFracGroups.map((frac, i) => {
                  const fracStyle = getFracColor(frac);
                  return (
                    <span key={i} className={`text-xs px-3 py-1.5 rounded-full font-medium ${fracStyle.bg} ${fracStyle.text}`}>
                      FRAC {frac}
                    </span>
                  );
                })}
              </div>
              <p className="text-xs text-purple-600 mt-3">
                Rotate FRAC groups to prevent fungicide resistance. Avoid consecutive applications of the same group.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Product Label Disclaimer */}
        <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
          <Warning className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800 dark:text-amber-200">
            <strong>Always read and follow the product label</strong> — it is the law. Application rates, timing, safety precautions, and restricted-use requirements vary by product. 
            Proper FRAC group rotation is essential to prevent fungicide resistance. The information below is for reference only — defer to the manufacturer's label for exact instructions.
          </p>
        </div>

        <Tabs defaultValue="calculator" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger data-testid="disease-calculator-tab" value="calculator">Fungicide Calculator</TabsTrigger>
            <TabsTrigger data-testid="disease-products-tab" value="products">Product Library</TabsTrigger>
            <TabsTrigger data-testid="disease-history-tab" value="history">Application History</TabsTrigger>
          </TabsList>

          {/* Calculator Tab */}
          <TabsContent value="calculator">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator weight="duotone" size={24} className="text-primary" />
                  Fungicide Application Calculator
                </CardTitle>
                <CardDescription>
                  Calculate product amount and cost — includes FRAC group for rotation planning
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Product Selection */}
                <div>
                  <Label>Select Fungicide Product</Label>
                  <div className="grid md:grid-cols-3 gap-4 mt-2">
                    {products.map((product) => {
                      const fracStyle = getFracColor(product.frac_group);
                      return (
                        <div
                          key={product.id}
                          data-testid={`disease-product-card-${product.id}`}
                          onClick={() => setSelectedProduct(product)}
                          className={`
                            border rounded-lg p-4 cursor-pointer transition-all
                            ${selectedProduct?.id === product.id
                              ? 'border-primary bg-primary/5 ring-2 ring-primary'
                              : 'border-border hover:border-primary/50'
                            }
                          `}
                        >
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span className={`text-xs px-2 py-1 rounded ${getTypeStyle(product.product_type)}`}>
                              {product.product_type}
                            </span>
                            <span className={`text-xs px-2 py-1 rounded ${fracStyle.bg} ${fracStyle.text}`}>
                              FRAC {product.frac_group}
                            </span>
                            {product.purchase_tier === 'consumer' && (
                              <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">DIY</span>
                            )}
                            {!product.purchase_tier && (
                              <span className="text-xs px-2 py-1 rounded bg-primary/10 text-primary">Pro</span>
                            )}
                          </div>
                          <h4 className="font-bold text-sm">{product.name}</h4>
                          <p className="text-xs text-muted-foreground mt-1">{product.active_ingredient}</p>
                          <p className="text-xs text-muted-foreground">
                            {product.application_rate_min}-{product.application_rate_max} {product.rate_unit || 'fl oz'}/1000 sq ft
                          </p>
                          {product.cost_per_unit && (
                            <div className="mt-2">
                              <p className="text-xs text-primary font-medium">
                                ${product.cost_per_unit.toFixed(2)} / {product.unit_size}
                              </p>
                              <p className="text-[10px] text-muted-foreground/70 italic">Price may vary</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {selectedProduct && (
                  <>
                    {/* FRAC Info Box */}
                    <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Info weight="bold" size={18} className="text-purple-600" />
                        <span className="font-medium text-purple-800">FRAC Group {selectedProduct.frac_group}</span>
                      </div>
                      <p className="text-sm text-purple-700">
                        {getFracColor(selectedProduct.frac_group).label}. Target diseases: <strong>{selectedProduct.target_diseases?.join(', ') || 'General'}</strong>
                      </p>
                      {recentFracGroups.includes(selectedProduct.frac_group) && (
                        <p className="text-sm text-red-600 font-medium mt-2">
                          Warning: FRAC {selectedProduct.frac_group} was recently applied. Consider rotating to a different group.
                        </p>
                      )}
                    </div>

                    {/* Area Input */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="disease-area">Treatment Area</Label>
                        <Input
                          id="disease-area"
                          data-testid="disease-area-input"
                          type="number"
                          step="0.01"
                          value={area}
                          onChange={(e) => setArea(e.target.value)}
                          placeholder="Enter area"
                        />
                      </div>
                      <div>
                        <Label htmlFor="disease-area-unit">Unit</Label>
                        <Select value={areaUnit} onValueChange={setAreaUnit}>
                          <SelectTrigger id="disease-area-unit" data-testid="disease-area-unit">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="sqft">Square Feet</SelectItem>
                            <SelectItem value="acres">Acres</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Application Rate */}
                    <div>
                      <Label htmlFor="disease-rate">Application Rate ({selectedProduct.rate_unit || 'fl oz'} per 1000 sq ft)</Label>
                      <Input
                        id="disease-rate"
                        data-testid="disease-rate-input"
                        type="number"
                        step="0.01"
                        value={applicationRate}
                        onChange={(e) => setApplicationRate(e.target.value)}
                        placeholder={`Recommended: ${selectedProduct.application_rate_min}-${selectedProduct.application_rate_max}`}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Recommended range: {selectedProduct.application_rate_min}-{selectedProduct.application_rate_max} {selectedProduct.rate_unit || 'fl oz'}/1000 sq ft
                      </p>
                    </div>

                    {/* Calculate Button */}
                    <Button data-testid="disease-calculate-btn" onClick={calculateApplication} className="w-full" size="lg">
                      <Calculator weight="bold" size={20} className="mr-2" />
                      Calculate Amount & Cost
                    </Button>

                    {/* Results */}
                    {calculatedAmount !== null && (
                      <div data-testid="disease-calc-results" className="bg-primary/5 border border-primary rounded-lg p-6 space-y-4">
                        <div>
                          <p className="text-sm text-muted-foreground">Product Needed</p>
                          <p className="text-3xl font-bold text-primary">
                            {calculatedAmount.toFixed(2)} {selectedProduct.rate_unit || 'fl oz'}
                          </p>
                          <p className="text-sm text-muted-foreground mt-1">
                            of {selectedProduct.name} (FRAC {selectedProduct.frac_group})
                          </p>
                        </div>

                        {calculatedCost !== null && (
                          <div className="border-t border-border pt-4">
                            <p className="text-sm text-muted-foreground">Estimated Cost</p>
                            <p className="text-2xl font-bold text-primary">
                              ${calculatedCost.toFixed(2)}
                            </p>
                          </div>
                        )}

                        <Button
                          data-testid="disease-log-from-calc-btn"
                          onClick={() => {
                            setAppArea(area);
                            setAppRate(applicationRate);
                            setShowApplicationDialog(true);
                          }}
                          variant="outline"
                          className="w-full"
                        >
                          Log This Application
                        </Button>
                        {selectedProduct?.affiliate_link && (
                          <a href={selectedProduct.affiliate_link} target="_blank" rel="nofollow sponsored noopener noreferrer" className="block">
                            <Button variant="secondary" className="w-full" data-testid="calc-buy-amazon">
                              Buy {selectedProduct.name} on Amazon
                            </Button>
                          </a>
                        )}
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <CardTitle>Fungicide Product Library</CardTitle>
                    <CardDescription>Preventive and curative fungicides with FRAC group classification</CardDescription>
                  </div>
                  <div className="relative w-full sm:w-64">
                    <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                    <Input
                      placeholder="Search products..."
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      className="pl-10"
                      data-testid="product-search-input"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredProducts.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    {productSearch ? `No products found matching "${productSearch}"` : 'No products available'}
                  </div>
                ) : (
                <div className="space-y-4">
                  {filteredProducts.map((product) => {
                    const fracStyle = getFracColor(product.frac_group);
                    return (
                      <div key={product.id} data-testid={`disease-product-detail-${product.id}`} className="border border-border rounded-lg p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <span className={`text-xs px-2 py-1 rounded ${getTypeStyle(product.product_type)}`}>
                                {product.product_type}
                              </span>
                              <span className={`text-xs px-2 py-1 rounded ${fracStyle.bg} ${fracStyle.text}`}>
                                FRAC {product.frac_group}
                              </span>
                            </div>
                            <h3 className="text-xl font-bold">{product.name}</h3>
                            <p className="text-sm text-muted-foreground">{product.manufacturer}</p>
                          </div>
                          {product.cost_per_unit && (
                            <div className="text-right">
                              <p className="text-2xl font-bold text-primary">${product.cost_per_unit.toFixed(2)}</p>
                              <p className="text-xs text-muted-foreground">per {product.unit_size}</p>
                              <p className="text-[10px] text-muted-foreground/60 italic">Price may vary — check retailer</p>
                            </div>
                          )}
                        </div>

                        <p className="text-sm text-foreground mb-4">{product.description}</p>

                        <div className="grid md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-muted-foreground">Active Ingredient</p>
                            <p className="font-medium">{product.active_ingredient}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Application Rate</p>
                            <p className="font-medium">
                              {product.application_rate_min}-{product.application_rate_max} {product.rate_unit || 'fl oz'}/1000 sq ft
                            </p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Target Diseases</p>
                            <p className="font-medium">{product.target_diseases?.join(', ') || 'General'}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Re-application</p>
                            <p className="font-medium">{product.reapplication_interval_days} days</p>
                          </div>
                        </div>

                        <div className="mt-4 flex gap-2">
                          <Button
                            onClick={() => {
                              setSelectedProduct(product);
                              document.querySelector('[value="calculator"]')?.click();
                            }}
                            variant="outline"
                            size="sm"
                          >
                            Use in Calculator
                          </Button>
                          {product.affiliate_link && (
                            <a href={product.affiliate_link} target="_blank" rel="nofollow sponsored noopener noreferrer">
                              <Button variant="secondary" size="sm" data-testid={`buy-amazon-${product.id}`}>
                                Buy on Amazon <span className="text-xs opacity-70">(paid link)</span>
                              </Button>
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle>Application History</CardTitle>
                <CardDescription>Your fungicide application records with FRAC rotation tracking</CardDescription>
              </CardHeader>
              <CardContent>
                {applications.length > 0 ? (
                  <div className="space-y-4">
                    {applications.map((app, idx) => {
                      const fracMatch = app.notes?.match(/FRAC (\w+)/);
                      const fracGroup = fracMatch ? fracMatch[1] : null;
                      const fracStyle = fracGroup ? getFracColor(fracGroup) : null;
                      // Check if same FRAC as previous application
                      const prevApp = applications[idx - 1];
                      const prevFrac = prevApp?.notes?.match(/FRAC (\w+)/)?.[1];
                      const sameAsPrev = fracGroup && prevFrac && fracGroup === prevFrac;

                      return (
                        <div key={app.id} className={`border rounded-lg p-4 ${sameAsPrev ? 'border-red-300 bg-red-50/50' : 'border-border'}`}>
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-bold">{app.product_name}</h4>
                                <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${app.application_method === 'granular' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'}`}>
                                  {(app.application_method || 'spray') === 'granular' ? 'Granular' : 'Spray'}
                                </span>
                                {fracStyle && (
                                  <span className={`text-xs px-2 py-0.5 rounded ${fracStyle.bg} ${fracStyle.text}`}>
                                    FRAC {fracGroup}
                                  </span>
                                )}
                                {app.zone_name && (
                                  <span className="text-xs px-2 py-0.5 rounded bg-secondary text-secondary-foreground">{app.zone_name}</span>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">{app.application_rate}</p>
                              {app.spreader_setting && (
                                <p className="text-xs text-muted-foreground">Spreader: {app.spreader_setting}</p>
                              )}
                              {app.notes && (
                                <p className="text-sm text-muted-foreground mt-1">{app.notes}</p>
                              )}
                              {sameAsPrev && (
                                <p className="text-xs text-red-600 font-medium mt-1">
                                  Same FRAC group as previous application — consider rotating
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-medium">{Math.round(app.gdd_at_application)} GDD</p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(app.applied_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <ShieldWarning weight="duotone" size={64} className="mx-auto text-muted-foreground/30" />
                    <p className="text-muted-foreground mt-4">No fungicide applications logged yet</p>
                    <Button data-testid="disease-log-first-btn" onClick={() => setShowApplicationDialog(true)} className="mt-4">
                      Log First Application
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        <AmazonDisclosure />
      </div>

      {/* Application Dialog */}
      <Dialog open={showApplicationDialog} onOpenChange={setShowApplicationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log Fungicide Application</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleLogApplication} className="space-y-4">
            {/* Application Method Toggle */}
            <div>
              <Label>Application Method</Label>
              <div className="flex gap-2 mt-1">
                <Button type="button" variant={appMethod === 'spray' ? 'default' : 'outline'} size="sm" className="flex-1" onClick={() => setAppMethod('spray')} data-testid="disease-method-spray-btn">
                  Spray
                </Button>
                <Button type="button" variant={appMethod === 'granular' ? 'default' : 'outline'} size="sm" className="flex-1" onClick={() => setAppMethod('granular')} data-testid="disease-method-granular-btn">
                  Granular
                </Button>
              </div>
            </div>

            <div>
              <Label>Product</Label>
              <Select
                value={selectedProduct?.id}
                onValueChange={(id) => setSelectedProduct(products.find(p => p.id === id))}
              >
                <SelectTrigger data-testid="disease-dialog-product-select">
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent>
                  {products.map((product) => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name} (FRAC {product.frac_group})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Area</Label>
                <Input
                  data-testid="disease-dialog-area"
                  type="number"
                  step="0.01"
                  value={appArea}
                  onChange={(e) => setAppArea(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label>Unit</Label>
                <Select value={areaUnit} onValueChange={setAreaUnit}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sqft">Sq Ft</SelectItem>
                    <SelectItem value="acres">Acres</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Rate ({appMethod === 'granular' ? 'lbs' : (selectedProduct?.rate_unit || 'fl oz')}/1000 sq ft)</Label>
              <Input
                data-testid="disease-dialog-rate"
                type="number"
                step="0.01"
                value={appRate}
                onChange={(e) => setAppRate(e.target.value)}
                required
              />
            </div>

            {appMethod === 'granular' && (
              <div>
                <Label>Spreader Setting (optional)</Label>
                <Input
                  value={appSpreaderSetting}
                  onChange={(e) => setAppSpreaderSetting(e.target.value)}
                  placeholder="e.g. 3.5 on Scotts EdgeGuard"
                  data-testid="disease-spreader-setting-input"
                />
              </div>
            )}

            <div>
              <Label>Notes (optional)</Label>
              <Input
                data-testid="disease-dialog-notes"
                value={appNotes}
                onChange={(e) => setAppNotes(e.target.value)}
                placeholder="Disease pressure, weather conditions, observations..."
              />
            </div>

            {zones.length > 0 && (
              <div>
                <Label>Zone</Label>
                <Select value={appZoneId} onValueChange={setAppZoneId}>
                  <SelectTrigger data-testid="disease-dialog-zone">
                    <SelectValue placeholder="Select zone (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {zones.map(z => (
                      <SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <Button data-testid="disease-dialog-submit" type="submit" className="w-full">
              Save Application
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      </UpgradeGate>
    </div>
  );
};
